import React from 'react';
import { Table } from '@tanstack/react-table';
import { columnSizeVar } from './constants';

export function useColumnSizing<T>({ table }: { table: Table<T> }) {
  const { columnSizingInfo, columnSizing, columnOrder, columnVisibility } =
    table.getState();
  const isResizingColumn = columnSizingInfo.isResizingColumn;

  /**
   * Instead of calling `column.getSize()` on every render for every header
   * and especially every data cell (very expensive),
   * we will calculate all column sizes at once at the root table level in a useMemo
   * and pass the column sizes down as CSS variables to the <table> element.
   * c.f. https://tanstack.com/table/latest/docs/guide/column-sizing
   *
   * columnOrder/columnVisibility are dependencies too: they change when a
   * column starts (or stops) appearing in `table.getFlatHeaders()`, e.g. as
   * custom field definitions load in after the table has mounted. Without
   * them the memo holds a stale header list and the new column gets no size
   * variable until an unrelated resize invalidates it.
   */
  const columnSizeVars = React.useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[columnSizeVar('header', header.id)] = header.getSize();
      colSizes[columnSizeVar('col', header.column.id)] = header.column.getSize();
    }
    return colSizes;
  }, [columnSizingInfo, columnSizing, columnOrder, columnVisibility]);

  return { columnSizeVars, isResizingColumn };
}
